import React, { useState } from "react";
import { useRouter } from "next/router";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Svg12 as ChevronDown } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown";
import { Center } from "@swiggy-private/dls-web-layouts/dist/components/Center";
import { BenefitsSection } from "./BenefitsSection";
import { stepsData, benefitsData } from "./data";
import { HeroSection } from "./HeroSection";
import { StepsSection } from "./StepsSection";
import { useAuth } from "../Auth";
import { useOTPTimer } from "../../hooks/useOTPTimer";
import { useOTPRetry } from "../../hooks/useOTPRetry";
import { useOnboarding, clearOnboardingStorage } from "../../contexts/OnboardingContext";
import { Container, ContentWrapper, DividerWrapper, GradientLine, MainBannerContainer } from "./styles";
import {
    logMobileNumberSubmitFailed,
    logMobileNumberSubmitted,
    logOTPEntered,
    logMobileNumberClick,
    logOTPErrorResponse,
    logContinueButtonClick,
    logResendOTPClick,
} from "./analytics";
import { getImageKitUrl } from "utils/cdn";
import { RemoteImages } from "constants/images";

const LandingPage = () => {
    const router = useRouter();
    const { login, verifyOtp, isLoading, error } = useAuth();
    const { resetOnboardingData } = useOnboarding();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [showOTP, setShowOTP] = useState(false);
    const [otpValue, setOtpValue] = useState("");
    const [sendOTPError, setSendOTPError] = useState<string | null>(null);

    // Use the OTP timer hook
    const { resendTimer, canResend, startTimer, formatTime } = useOTPTimer({
        initialDuration: 30,
        onTimerComplete: () => {},
    });

    // Use the OTP retry hook
    const {
        totalAttemptsRemaining,
        isInCooldown,
        cooldownTimeRemaining,
        canAttemptOTP,
        recordOTPAttempt,
        formatCooldownTime,
    } = useOTPRetry({
        maxTotalAttempts: 5,
        cooldownDuration: 900, // 15 minutes
        storageKey: "otp_retry_data",
        phoneNumber: phoneNumber,
    });

    const isButtonDisabled = phoneNumber.length !== 10;
    const isOTPButtonDisabled = otpValue.length !== 6 || !canAttemptOTP;

    const handleGetStarted = async () => {
        try {
            logMobileNumberSubmitted(phoneNumber);
            setSendOTPError(null); // Clear previous errors
            // Clear any previous onboarding data when starting fresh
            clearOnboardingStorage();
            resetOnboardingData(); // Also reset context state
            await login(phoneNumber);
            setShowOTP(true);
            startTimer(); // Start the 30-second timer
            // Don't record attempt on successful OTP send - only count verification attempts
        } catch (error) {
            logMobileNumberSubmitFailed(phoneNumber, error instanceof Error ? error.message : "Failed to send OTP");
            logOTPErrorResponse(error instanceof Error ? error.message : "Failed to send OTP");
            setSendOTPError(error instanceof Error ? error.message : "Failed to send OTP");
            // Don't record attempt on send failure - only count verification attempts
        }
    };

    const handleOTPChange = (value: string) => {
        setOtpValue(value);
        if (value.length === 6) {
            logOTPEntered();
        }
    };

    const handleResendOTP = async () => {
        if (!canResend || !canAttemptOTP) return; // Block resend if verification attempts exhausted

        logResendOTPClick();
        try {
            setSendOTPError(null); // Clear previous errors
            await login(phoneNumber);
            startTimer(); // Reset to 30 seconds
            // Don't record attempt on OTP resend - only count verification attempts
        } catch (error) {
            console.error("Failed to resend OTP:", error);
            logOTPErrorResponse(error instanceof Error ? error.message : "Failed to resend OTP");
            setSendOTPError(error instanceof Error ? error.message : "Failed to resend OTP");
            // Don't record attempt on resend failure - only count verification attempts
        }
    };

    const handleContinue = async () => {
        logContinueButtonClick();
        try {
            await verifyOtp(otpValue);
            router.push("/home");
        } catch (error) {
            console.error("Failed to sign in:", error);
            logOTPErrorResponse(error instanceof Error ? error.message : "Failed to sign in");
            // Record OTP attempt on verification failure
            recordOTPAttempt();
        }
    };

    const handleModifyPhone = () => {
        setShowOTP(false);
        setOtpValue("");
        setSendOTPError(null);
        // Reset OTP attempts when changing phone number
    };

    const handlePhoneBlur = () => {
        if (phoneNumber.length > 0) {
            logMobileNumberClick();
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <Container data-testid="landing-page-container">
            <MainBannerContainer>
                <Image
                    src={getImageKitUrl(RemoteImages.mainBackgroundBanner, {
                        width: 1024,
                    })}
                    alt="Main Background Banner"
                    width="100%"
                    height="100%"
                    fit="cover"
                />
            </MainBannerContainer>
            <ContentWrapper>
                <HeroSection
                    phoneNumber={phoneNumber}
                    onPhoneChange={setPhoneNumber}
                    onPhoneBlur={handlePhoneBlur}
                    onGetStarted={handleGetStarted}
                    isButtonDisabled={isButtonDisabled || isLoading}
                    showOTP={showOTP}
                    otpValue={otpValue}
                    onOTPChange={handleOTPChange}
                    onResendOTP={handleResendOTP}
                    onContinue={handleContinue}
                    isOTPButtonDisabled={isOTPButtonDisabled || isLoading}
                    resendTimer={resendTimer}
                    canResend={canResend && canAttemptOTP}
                    formatTime={formatTime}
                    error={error}
                    sendOTPError={sendOTPError}
                    onModifyPhone={handleModifyPhone}
                    totalAttemptsRemaining={totalAttemptsRemaining}
                    isInCooldown={isInCooldown}
                    cooldownTimeRemaining={cooldownTimeRemaining}
                    formatCooldownTime={formatCooldownTime}
                />

                <Center flexDirection="column" vertical>
                    <GenericIcon
                        ariaLabel="Chevron Down Icon"
                        Icon={ChevronDown}
                        strokeColor={"text_Highest_Emphasis"}
                        fillColor="positive"
                    />
                    <GenericIcon
                        ariaLabel="Chevron Down Icon"
                        Icon={ChevronDown}
                        strokeColor={"text_Highest_Emphasis"}
                        fillColor="positive"
                        style={{ opacity: 0.5 }}
                    />
                </Center>
                <StepsSection stepsData={stepsData} onReferNowClick={scrollToTop} />

                <DividerWrapper>
                    <GradientLine />
                </DividerWrapper>

                <BenefitsSection benefitsData={benefitsData} />
            </ContentWrapper>
        </Container>
    );
};

export default LandingPage;
